import type { DocumentSubject } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'

import {
  DEFAULT_TARGET_PAGE,
  NavigationTargetCandidate,
} from './navigationTargetResolver'

/** Collapse OCR reference whitespace; matching is case-insensitive on this form. */
export function normalizeReferenceText(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * The 10-digit ADNOC drawing core (NN NN NN NNNN) embedded in a reference,
 * separator-agnostic, or null if the text carries no such core.
 *
 * The same drawing is printed with different separators depending on where it
 * appears: an off-page connector reads `44.01.08.0002` (dots), while the home
 * sheet's title-block `drawing_number` is stored hyphenated (`44-01-08-0002`),
 * suffixed (`44-01-08-0002-01`), or glued (`4401080002`). Reducing all of them
 * to the bare `4401080002` core lets the click resolve across those forms. A
 * trailing sheet/rev segment is ignored (only the 2-2-2-4 core is captured).
 *
 * Both ends are digit-boundary guarded so a longer glued run (e.g.
 * `1234401080002`) can't have a `4401080002` core sliced out of its middle and
 * masquerade as `44.01.08.0002`.
 */
const DRAWING_CORE_RE =
  /(?<!\d)(\d{2})[.\-\s]?(\d{2})[.\-\s]?(\d{2})[.\-\s]?(\d{4})(?!\d)/

export function drawingNumberCore(text: string): string | null {
  const match = normalizeReferenceText(text).match(DRAWING_CORE_RE)
  return match ? match.slice(1, 5).join('') : null
}

/**
 * Separator variants of a reference for the Rolodex `drawing_number` prefilter:
 * the raw text plus dotted / hyphenated / glued forms of its numeric core, so a
 * literal (separator-sensitive) REGEX search still finds the stored number
 * whatever separator it was saved with. Non-core references pass through as-is.
 */
export function drawingNumberSearchVariants(text: string): string[] {
  const core = drawingNumberCore(text)
  const out = [text]
  if (core) {
    const [a, b, c, d] = [
      core.slice(0, 2),
      core.slice(2, 4),
      core.slice(4, 6),
      core.slice(6),
    ]
    out.push(`${a}.${b}.${c}.${d}`, `${a}-${b}-${c}-${d}`, core)
  }
  return [...new Set(out.map((t) => t.trim()).filter(Boolean))]
}

export type DocReferenceResolverInput = {
  referenceText: string
  currentDocumentId: string
  systemDocumentEntries: SystemDocumentEntry[]
}

/** OPC sheet-local suffix vs title-block drawing number (segment-boundary match). */
function isBoundarySuffixMatch(
  drawingNumber: string,
  referenceText: string
): boolean {
  if (!drawingNumber.endsWith(referenceText)) return false
  if (drawingNumber.length === referenceText.length) return true
  const boundaryChar =
    drawingNumber[drawingNumber.length - referenceText.length - 1]
  return !/[a-z0-9]/i.test(boundaryChar)
}

/** Resolution texts for a doc-reference click: canonical_tag aliases first, then label. */
export function docReferenceResolutionTexts(
  subject: Pick<DocumentSubject, 'aliases'> | undefined,
  clickedLabel: string
): string[] {
  const ordered = [
    ...(subject?.aliases ?? [])
      .filter((alias) => alias.kind === 'canonical_tag')
      .map((alias) => alias.value),
    clickedLabel,
    ...(subject?.aliases ?? []).map((alias) => alias.value),
  ]
  const seen = new Set<string>()
  const texts: string[] = []
  for (const raw of ordered) {
    const text = normalizeReferenceText(raw)
    if (!text || seen.has(text.toLowerCase())) continue
    seen.add(text.toLowerCase())
    texts.push(text)
  }
  return texts
}

/** Current sheet's own drawing number — identity, not a failed lookup. */
export function isSelfReference(
  referenceText: string,
  currentDrawingNumber: string | null | undefined
): boolean {
  const needle = normalizeReferenceText(referenceText).toLowerCase()
  const drawingNumber = normalizeReferenceText(
    currentDrawingNumber ?? ''
  ).toLowerCase()
  if (!needle || !drawingNumber) return false
  if (
    drawingNumber === needle ||
    isBoundarySuffixMatch(drawingNumber, needle)
  ) {
    return true
  }
  // Separator-agnostic: a dotted OPC is self-referencing the hyphenated/glued
  // title-block number of the same sheet.
  const needleCore = drawingNumberCore(needle)
  return needleCore !== null && needleCore === drawingNumberCore(drawingNumber)
}

type ResolvedCandidate = {
  document: SystemDocumentEntry
  documentId: string
}

function toCandidate({
  document,
  documentId,
}: ResolvedCandidate): NavigationTargetCandidate {
  return {
    documentId,
    // String match is document-level only.
    pageNumber: DEFAULT_TARGET_PAGE,
    revisionCode: document.entry.data.revision_code,
  }
}

/**
 * Resolve doc-reference text to navigable documents: exact drawing_number,
 * separator-agnostic numeric-core drawing_number, boundary-suffix drawing_number,
 * then document_title containment. Excludes current document.
 */
export function resolveDocReferenceTargets({
  referenceText,
  currentDocumentId,
  systemDocumentEntries,
}: DocReferenceResolverInput): NavigationTargetCandidate[] {
  const needle = normalizeReferenceText(referenceText).toLowerCase()
  if (!needle) return []
  const needleCore = drawingNumberCore(needle)

  const candidates: ResolvedCandidate[] = []
  for (const document of systemDocumentEntries) {
    const data = document.entry.data
    // A spreadsheet's drawing_number can cite a P&ID; it is not the connector destination.
    if (
      /\.(xlsx?|csv|tsv)$/i.test(data.filename ?? data.title ?? '') ||
      /spreadsheet|excel/i.test(data.type ?? '')
    )
      continue
    const documentId = getSystemDocumentBqId(document)
    if (!documentId) continue
    if (
      documentId === currentDocumentId ||
      document.entry.id === currentDocumentId
    ) {
      continue
    }
    candidates.push({ document, documentId })
  }

  const exactMatches: ResolvedCandidate[] = []
  const suffixMatches: ResolvedCandidate[] = []
  const titleMatches: ResolvedCandidate[] = []

  for (const candidate of candidates) {
    const { document } = candidate
    const drawingNumber = normalizeReferenceText(
      document.entry.data.drawing_number ?? ''
    ).toLowerCase()
    // Match document_title, not source filename in title.
    const title = normalizeReferenceText(
      document.entry.data.title ?? ''
    ).toLowerCase()
    const documentTitle = normalizeReferenceText(
      document.entry.data.document_title ?? ''
    ).toLowerCase()

    const drawingCore = drawingNumberCore(drawingNumber)

    if (drawingNumber && drawingNumber === needle) {
      exactMatches.push(candidate)
    } else if (needleCore && drawingCore && needleCore === drawingCore) {
      // Same drawing, different separators (44.01.08.0002 ⇄ 44-01-08-0002 ⇄
      // 4401080002 ⇄ 44-01-08-0002-01) — a full-identity match, not a suffix.
      exactMatches.push(candidate)
    } else if (drawingNumber && isBoundarySuffixMatch(drawingNumber, needle)) {
      suffixMatches.push(candidate)
    } else if (
      (title && title.includes(needle)) ||
      (documentTitle && documentTitle.includes(needle))
    ) {
      titleMatches.push(candidate)
    }
  }

  const best =
    exactMatches.length > 0
      ? exactMatches
      : suffixMatches.length > 0
        ? suffixMatches
        : titleMatches

  return best.map(toCandidate)
}
